import React from 'react';
import { Search, Sun, Moon, Sparkles, User as UserIcon, LogIn, Bell } from 'lucide-react';
import { User, TabType } from '../../types';
import styles from './Navbar.module.css';

interface NavbarProps {
  user: User | null;
  activeTab: TabType;
  setActiveTab: (tab: TabType) => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  onOpenAuth: () => void;
  onOpenSettings: () => void;
  unreadNotifsCount: number;
}

export const Navbar: React.FC<NavbarProps> = ({
  user,
  setActiveTab,
  searchQuery,
  setSearchQuery,
  onOpenAuth,
  onOpenSettings,
  unreadNotifsCount,
}) => {
  const [isLight, setIsLight] = React.useState(false);

  const toggleColorScheme = () => {
    const next = !isLight;
    setIsLight(next);
    document.documentElement.setAttribute('data-color-scheme', next ? 'light' : 'dark');
  };

  return (
    <header className={styles.header}>
      {/* Brand & Logo */}
      <div className={styles.brand} onClick={() => setActiveTab('home')}>
        <div className={styles.logoIcon}>
          <svg width="22" height="22" viewBox="0 0 100 100" fill="none">
            <path
              d="M30 35 C30 25, 45 20, 60 25 C75 30, 75 45, 60 50 C45 55, 30 55, 30 70 C30 80, 50 82, 70 75"
              stroke="white"
              strokeWidth="12"
              strokeLinecap="round"
            />
            <circle cx="70" cy="30" r="7" fill="#34d399" />
          </svg>
        </div>
        <span className={styles.brandName}>Cordle</span>
      </div>

      {/* Global Instant Search */}
      <div className={styles.searchContainer}>
        <Search className={styles.searchIcon} />
        <input
          type="text"
          className={styles.searchInput}
          placeholder="Search posts, #tags, or @users..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {/* Actions & User State */}
      <div className={styles.actions}>
        <button className={styles.iconBtn} onClick={toggleColorScheme} title="Toggle Light/Dark Theme">
          {isLight ? <Moon size={18} /> : <Sun size={18} />}
        </button>

        {user ? (
          <>
            <button
              className={styles.iconBtn}
              onClick={() => setActiveTab('notifications')}
              title="Notifications"
              style={{ position: 'relative' }}
            >
              <Bell size={18} />
              {unreadNotifsCount > 0 && (
                <span
                  style={{
                    position: 'absolute',
                    top: '4px',
                    right: '4px',
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    background: 'var(--status-dnd)',
                  }}
                />
              )}
            </button>

            <div className={styles.userBadge} onClick={() => setActiveTab('profile')}>
              <img src={user.avatar_url} alt={user.display_name} className={styles.avatar} />
              <span className={styles.username}>{user.display_name}</span>
            </div>
          </>
        ) : (
          <button className={styles.authBtn} onClick={onOpenAuth}>
            Sign In / Register
          </button>
        )}
      </div>
    </header>
  );
};
